import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface EmailCodeChallengeProps {
  emailHint: string;
  busy: boolean;
  error?: string;
  onSubmit: (code: string) => void;
  onCancel: () => void;
}

/**
 * Second factor for accounts that have no passkey yet: the 6-digit code
 * emailed by the CreateAuthChallenge trigger. After it passes, LoginPage
 * makes the user enroll a passkey before entering the app.
 */
export const EmailCodeChallenge: React.FC<EmailCodeChallengeProps> = ({ emailHint, busy, error, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const valid = /^\d{6}$/.test(code);

  return (
    <div className="email-code-container">
      <form
        className="email-code-card"
        onSubmit={(e) => { e.preventDefault(); if (valid && !busy) onSubmit(code); }}
      >
        <h2>{t("login.emailCodeTitle")}</h2>
        <p>{t("login.emailCodeSubtitle", { email: emailHint })}</p>
        <input
          className="email-code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          placeholder="••••••"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          aria-label={t("login.resetCode")}
        />
        {error && <p className="email-code-error">{error}</p>}
        <button className="email-code-submit" type="submit" disabled={!valid || busy}>
          {busy ? t("login.signingIn") : t("login.emailCodeVerify")}
        </button>
        <button className="email-code-cancel" type="button" onClick={onCancel}>
          {t("login.backToLogin")}
        </button>
      </form>

      <style>{`
        .email-code-container {
          min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px;
          background: linear-gradient(145deg, var(--color-surface) 0%, var(--color-bg) 50%, var(--color-accent-ghost) 100%);
        }
        .email-code-card {
          background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 16px;
          box-shadow: var(--shadow-md); padding: 40px; width: 100%; max-width: 400px; text-align: center;
          display: flex; flex-direction: column; gap: 14px;
        }
        .email-code-card h2 { font-family: var(--font-display); font-size: 24px; color: var(--color-title); margin: 0; }
        .email-code-card p { font-size: 14px; color: var(--color-muted); margin: 0; line-height: 1.5; }
        .email-code-input {
          font-size: 28px; letter-spacing: 0.4em; text-align: center; padding: 12px; font-variant-numeric: tabular-nums;
          border: 1px solid var(--color-border-strong); border-radius: 10px; background: var(--color-bg); color: var(--color-ink);
        }
        .email-code-input:focus { outline: none; border-color: var(--color-border-focus); box-shadow: var(--shadow-focus); }
        .email-code-error { color: var(--color-danger) !important; }
        .email-code-submit {
          padding: 12px; border-radius: 10px; border: none; font-size: 15px; font-weight: 600; cursor: pointer;
          background: var(--color-brand); color: #fff;
        }
        .email-code-submit:disabled { opacity: 0.5; cursor: default; }
        .email-code-cancel { background: none; border: none; color: var(--color-muted); cursor: pointer; font-size: 13px; }
      `}</style>
    </div>
  );
};
